/**
 * Shared utility functions for email viewing components
 * Used by ThreadViewer and related components
 */

import { isCapacitor } from '@/lib/platformUtils';
import { getEndpointUrl } from '@/lib/apiConfig';
import { decodeMimeHeader } from '@/lib/mimeHeader';

// Cloud storage URL from environment
const CLOUD_STORAGE_URL = process.env.NEXT_PUBLIC_CLOUD_STORAGE_URL || 'https://cloud-storage.totalaccess.co.za';

/** Minimal shape for CID / inline attachment resolution */
export type EmailAttachmentLike = {
  directUrl?: string;
  url?: string;
  storage_token?: string;
  contentId?: string;
  content_id?: string;
  cid?: string;
  headers?: Record<string, string | undefined>;
};

export type SanitizeEmailHtmlOptions = {
  attachments?: EmailAttachmentLike[];
  /** Sender domain (e.g. standardbank.co.za) — used to resolve root-relative image URLs */
  senderDomain?: string;
};

/**
 * Check if content is encrypted and return appropriate message
 */
export function isEncryptedContent(content: unknown): boolean {
  return typeof content === 'object' && content !== null && 'encrypted' in content
    && (content as { encrypted?: boolean }).encrypted === true;
}

/**
 * Get displayable content - handles both strings and encrypted objects
 */
export function getDisplayableContent(content: unknown, fallback: string = ''): string {
  if (!content) return fallback;
  if (typeof content === 'string') return content;
  if (isEncryptedContent(content)) {
    return '<div class="p-4 text-center text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg"><span class="inline-flex items-center gap-2">🔐 This message is encrypted. Decryption keys are not available on this server.</span></div>';
  }
  return fallback;
}

/**
 * Parse a single email string like "Name <email@example.com>" or "<email@example.com>"
 */
export function parseEmailString(emailStr: string): { name?: string; address?: string; email?: string } {
  if (!emailStr) return {};

  // Decode RFC 2047 encoded-words in the header before parsing.
  const decoded = decodeMimeHeader(emailStr);

  // Match "Name <email>" — only when angle brackets are present; a bare
  // email like user82@gmail.com must not be split at the last char.
  const angle = decoded.match(/<([^<>]+@[^<>]+)>/);
  if (angle) {
    const name = decoded.slice(0, angle.index).trim().replace(/^['"]+|['"]+$/g, '') || undefined;
    const address = angle[1].trim();
    return { name, address, email: address };
  }

  // Fallback - return as-is
  return { address: decoded, email: decoded };
}

/**
 * Parse email recipients from various formats:
 * - Array of objects: [{ name: 'Name', address: 'email@example.com' }]
 * - String: "Name <email@example.com>" or "email@example.com"
 * - Undefined/null
 * Returns a normalized array of { name?, address, email? } objects
 */
export function parseEmailRecipients(recipients: unknown): Array<{ name?: string; address?: string; email?: string }> {
  if (!recipients) return [];

  // Already an array - normalize each entry
  if (Array.isArray(recipients)) {
    return recipients.map(r => {
      if (typeof r === 'string') {
        return parseEmailString(r);
      }
      // Check multiple possible name fields (different email formats use different field names)
      const name = decodeMimeHeader(r.name || r.displayName || r.display_name || undefined);
      const address = r.address || r.email;
      return {
        name: name && name !== address ? name : undefined,  // Don't use name if it's just the email
        address,
        email: r.email || r.address
      };
    });
  }

  // String format: "Name <email@example.com>" or just "email@example.com"
  if (typeof recipients === 'string') {
    // Split by comma for multiple recipients
    return decodeMimeHeader(recipients).split(',').map(s => parseEmailString(s.trim())).filter(r => r.address || r.email);
  }

  return [];
}

function normalizeCidKey(raw: unknown): string[] {
  if (raw == null || raw === '') return [];
  const s = String(raw).trim().replace(/^<|>$/g, '');
  if (!s) return [];
  return [s, `<${s}>`];
}

function getAttachmentCidKeys(att: EmailAttachmentLike): string[] {
  const keys: string[] = [];
  for (const raw of [
    att?.contentId,
    att?.content_id,
    att?.cid,
    att?.headers?.['content-id'],
    att?.headers?.['Content-ID'],
  ]) {
    keys.push(...normalizeCidKey(raw));
  }
  return [...new Set(keys.filter(Boolean))];
}

/**
 * Resolve CID references in email HTML to actual attachment URLs
 * This re-embeds inline images that were stored separately for encryption
 */
export function resolveCidReferences(html: string, attachments?: EmailAttachmentLike[]): string {
  if (!html || !attachments || attachments.length === 0) return html;

  let resolved = html;

  // Build a map of contentId -> URL for quick lookup
  const cidMap = new Map<string, string>();

  for (const att of attachments) {
    const cidKeys = getAttachmentCidKeys(att);
    if (cidKeys.length === 0) continue;

    // Get the URL for this attachment
    // On Capacitor, prefer public storage_token URLs over auth-required API URLs
    // because <img> tags in the sandboxed iframe can't send Authorization headers.
    let url: string | undefined;
    const rawUrl = att.directUrl || att.url;
    const isRelativeApi = rawUrl && (rawUrl.startsWith('/api/') || rawUrl.startsWith('/files/'));

    if (isCapacitor() && isRelativeApi && att.storage_token) {
      // Use public cloud storage URL to avoid auth issues in the iframe
      url = `${CLOUD_STORAGE_URL}/api/v1/attachments/${att.storage_token}`;
    } else {
      url = rawUrl;
      if (!url && att.storage_token) {
        url = `${CLOUD_STORAGE_URL}/api/v1/attachments/${att.storage_token}`;
      }
    }
    if (!url) continue;

    // Handle /files/ paths (public, no auth needed)
    if (url.startsWith('/files/')) {
      url = `${CLOUD_STORAGE_URL}${url}`;
    }

    // On Capacitor, resolve any remaining relative /api/ URLs to absolute
    if (isCapacitor() && url.startsWith('/api/')) {
      url = getEndpointUrl(url);
    }

    for (const k of cidKeys) {
      cidMap.set(k.replace(/^<|>$/g, ''), url);
      cidMap.set(`<${k.replace(/^<|>$/g, '')}>`, url);
    }
  }

  // Replace cid: references with actual URLs
  // Matches: cid:xxx in src attributes
  resolved = resolved.replace(/src\s*=\s*["']cid:([^"']+)["']/gi, (match, cid) => {
    const trimmed = String(cid).trim();
    const url = cidMap.get(trimmed) || cidMap.get(trimmed.replace(/^<|>$/g, ''));
    if (url) {
      return `src="${url}"`;
    }
    return match;
  });

  // Also handle url(cid:xxx) in CSS styles (less common but possible)
  resolved = resolved.replace(/url\(\s*["']?cid:([^"')]+)["']?\s*\)/gi, (match, cid) => {
    const trimmed = String(cid).trim();
    const url = cidMap.get(trimmed) || cidMap.get(trimmed.replace(/^<|>$/g, ''));
    if (url) {
      return `url("${url}")`;
    }
    return match;
  });

  return resolved;
}

function stripExecutableContent(html: string): string {
  let out = html;
  out = out.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  // Self-closing or unclosed <script> tags the paired-block regex misses. A
  // lone <script src> would swallow the rest of the document while parsing
  // and fires "Blocked script execution in 'about:srcdoc'" inside the
  // sandboxed viewer iframe.
  out = out.replace(/<script\b[^>]*\/?>/gi, '');
  out = out.replace(/<\/script\s*>/gi, '');
  out = out.replace(/\son\w+\s*=\s*["'][^"']*["']/gi, '');
  out = out.replace(/\son\w+\s*=\s*[^\s>]+/gi, '');
  // "/on…=" handlers — legal attribute separators after a quoted value or
  // directly after a tag name (e.g. <img src="x"/onerror=…>, <svg/onload=…>).
  out = out.replace(/(["'])\s*\/\s*on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '$1');
  out = out.replace(/(<[a-zA-Z][\w:-]*)\/on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '$1');
  // javascript:/vbscript: navigation URIs can never run inside the sandboxed
  // viewer — replace with inert anchors instead of blocked-script noise.
  out = out.replace(
    /(\s(?:href|action|formaction|xlink:href)\s*=\s*["'])\s*(?:javascript|vbscript|livescript|mocha)\s*:[^"']*/gi,
    '$1#',
  );
  return out;
}

/**
 * Remove elements/CSS that trigger external fetches the app CSP will block
 * anyway (remote fonts, stylesheets) or that must never load when quoted mail
 * is injected into the page DOM by the composer (iframes to arbitrary origins
 * are allowed by frame-src https:, and object/embed by plugin content).
 * Remote <img>/<video> URLs are left alone — img-src/media-src permit https:
 * and the viewer intentionally renders remote images.
 */
export function stripExternalResourceRefs(html: string): string {
  if (!html) return html;
  let out = html;
  out = out.replace(/<link\b[^>]*\/?>/gi, '');
  out = out.replace(/<base\b[^>]*\/?>/gi, '');
  out = out.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
  out = out.replace(/<\/?iframe\b[^>]*\/?>/gi, '');
  out = out.replace(/<\/(object|embed|applet)>/gi, '');
  out = out.replace(/<(object|embed|applet)\b[^>]*\/?>/gi, '');
  out = out.replace(/<style\b([^>]*)>([\s\S]*?)<\/style>/gi, (_match, attrs: string, css: string) => {
    const cleaned = css
      .replace(/@import\s+(?:url\s*\([^)]*\)|["'][^"']*["'])[^;]*;?/gi, '')
      .replace(/@font-face\s*\{[^{}]*\}/gi, '');
    return cleaned.trim() ? `<style${attrs}>${cleaned}</style>` : '';
  });
  return out;
}

/**
 * Root-relative and protocol-relative URLs often break when HTML is injected into the app shell.
 */
function rewriteImageUrlsForDisplay(html: string, senderDomain?: string): string {
  let out = html;
  // Protocol-relative -> https
  out = out.replace(/(\ssrc\s*=\s*["'])\/\//gi, '$1https://');
  out = out.replace(/(\shref\s*=\s*["'])\/\//gi, '$1https://');
  out = out.replace(/(url\(\s*["']?)\/\//gi, '$1https://');

  const domain = senderDomain?.trim().replace(/^@/, '').toLowerCase();
  if (!domain) return out;

  const base = `https://${domain}`;
  // src="/foo" -> absolute (quoted)
  out = out.replace(
    /(\ssrc\s*=\s*["'])\/([^"']*)(["'])/gi,
    (_, a: string, path: string, q: string) => `${a}${base}/${path.replace(/^\//, '')}${q}`
  );
  // background url("/foo")
  out = out.replace(
    /(url\(\s*["']?)\/([^"')]+)(["']?\s*\))/gi,
    (m, a: string, path: string, b: string) => {
      if (/^https?:/i.test(path)) return m;
      return `${a}${base}/${String(path).replace(/^\//, '')}${b}`;
    }
  );
  return out;
}

function extractBodyInnerHtml(html: string): string {
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch) return bodyMatch[1];
  return html;
}

/**
 * Wrap each top-level <table> in a scrollable container so wide tables can
 * overflow horizontally instead of forcing columns to compress to zero width.
 * Nested tables are left untouched because we only wrap when depth drops back
 * to zero.
 */
function wrapTopLevelTables(html: string): string {
  const tagRe = /<(table|\/table)\b[^>]*>/gi;
  const out: string[] = [];
  let lastIndex = 0;
  let depth = 0;
  let tableStart = -1;

  let match: RegExpExecArray | null;
  while ((match = tagRe.exec(html)) !== null) {
    const full = match[0];
    const tag = match[1].toLowerCase();
    const idx = match.index;

    if (tag === 'table') {
      if (depth === 0) {
        tableStart = idx;
      }
      depth++;
    } else {
      if (depth > 0) depth--;
      if (depth === 0 && tableStart !== -1) {
        const endIdx = idx + full.length;
        out.push(html.slice(lastIndex, tableStart));
        out.push(`<div style="overflow-x:auto;max-width:100%;">${html.slice(tableStart, endIdx)}</div>`);
        lastIndex = endIdx;
        tableStart = -1;
      }
    }
  }

  out.push(html.slice(lastIndex));
  return out.join('');
}

/**
 * Sanitize email HTML to fix mixed content, CID images, and layout inside our viewer.
 */
export function sanitizeEmailHtml(html: unknown, options?: SanitizeEmailHtmlOptions): string {
  const attachments = options?.attachments;
  const senderDomain = options?.senderDomain;

  // Handle non-string inputs (e.g., encrypted content objects)
  if (!html) return '';
  if (typeof html !== 'string') {
    return getDisplayableContent(html, '');
  }

  let sanitized = html;
  sanitized = extractBodyInnerHtml(sanitized);

  sanitized = sanitized.replace(/<meta[^>]*\/?>/gi, '');
  sanitized = sanitized.replace(/<!DOCTYPE[^>]*>/gi, '');
  sanitized = sanitized.replace(/<\/?html[^>]*>/gi, '');
  sanitized = sanitized.replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '');
  sanitized = sanitized.replace(/<\/?body[^>]*>/gi, '');
  sanitized = sanitized.replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '');

  sanitized = stripExecutableContent(sanitized);
  sanitized = stripExternalResourceRefs(sanitized);

  // Upgrade mixed content for images / links
  sanitized = sanitized.replace(/(<img[^>]+src=["'])http:\/\//gi, '$1https://');
  sanitized = sanitized.replace(/(\shref=["'])http:\/\//gi, '$1https://');

  sanitized = rewriteImageUrlsForDisplay(sanitized, senderDomain);

  // Wrap top-level tables so they scroll horizontally on small screens instead of
  // crushing narrow columns and causing headers like "Status" to render vertically.
  sanitized = wrapTopLevelTables(sanitized);

  sanitized = resolveCidReferences(sanitized, attachments);

  // Make links actionable in the sandboxed iframe: open external URLs (and mailto/tel)
  // in a new browsing context so they don't navigate inside the tiny preview frame.
  sanitized = ensureEmailLinksOpenInNewTab(sanitized);

  // On Capacitor, resolve any remaining relative URLs to absolute
  // so they work inside the srcDoc iframe (which has no base URL).
  if (isCapacitor()) {
    sanitized = resolveRelativeUrlsForCapacitor(sanitized);
  }

  return sanitized;
}

/**
 * On Capacitor, the srcDoc iframe has no base URL, so relative URLs like
 * /api/... or /files/... won't resolve.  Convert them to absolute URLs.
 * - /files/ paths go to CLOUD_STORAGE_URL (public, no auth)
 * - /api/v1/attachments/ paths go to CLOUD_STORAGE_URL (public, no auth)
 * - Other /api/ paths go to the app server via getEndpointUrl (may need auth)
 */
function resolveRelativeUrlsForCapacitor(html: string): string {
  let out = html;

  // Resolve src="..." attributes
  out = out.replace(/(\ssrc\s*=\s*["'])(\/?[^"']+)(["'])/gi, (_, prefix: string, url: string, suffix: string) => {
    return `${prefix}${resolveRelativeUrl(url)}${suffix}`;
  });

  // Resolve url("...") in CSS
  out = out.replace(/(url\(\s*["']?)(\/?[^"')]+)(["']?\s*\))/gi, (_, prefix: string, url: string, suffix: string) => {
    return `${prefix}${resolveRelativeUrl(url)}${suffix}`;
  });

  return out;
}

function resolveRelativeUrl(url: string): string {
  // Already absolute
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('cid:')) {
    return url;
  }
  // /files/ paths → cloud storage (public)
  if (url.startsWith('/files/')) {
    return `${CLOUD_STORAGE_URL}${url}`;
  }
  // /api/v1/attachments/ paths → cloud storage (public, token-based)
  if (url.startsWith('/api/v1/attachments/')) {
    return `${CLOUD_STORAGE_URL}${url}`;
  }
  // Other /api/ paths → app server (may need auth)
  if (url.startsWith('/api/')) {
    return getEndpointUrl(url);
  }
  // Other root-relative URLs → try app server
  if (url.startsWith('/')) {
    return getEndpointUrl(url);
  }
  return url;
}

/**
 * Ensure <a href="http(s)..."> (and mailto:/tel:) open in a new tab/window from the viewer iframe.
 * Marketing emails often omit target="_blank", which loads inside the iframe and appears "broken".
 */
function ensureEmailLinksOpenInNewTab(html: string): string {
  return html.replace(/<a\b([^>]*?)>/gi, (full, attrs: string) => {
    const hrefMatch =
      attrs.match(/\bhref\s*=\s*"([^"]*)"/i)
      || attrs.match(/\bhref\s*=\s*'([^']*)'/i)
      || attrs.match(/\bhref\s*=\s*([^\s>]+)/i);
    const hrefRaw = hrefMatch?.[1]?.trim() ?? '';
    const href = hrefRaw.replace(/^&quot;|&quot;$/g, '').trim();
    if (!href || href.startsWith('#') || /^javascript:/i.test(href)) {
      return full;
    }
    const opensSeparateContext =
      /^https?:\/\//i.test(href)
      || /^mailto:/i.test(href)
      || /^tel:/i.test(href)
      || /^sms:/i.test(href)
      || /^ftp:/i.test(href);
    if (!opensSeparateContext) {
      return full;
    }

    let nextAttrs = attrs.trim();
    if (!/\btarget\s*=/i.test(nextAttrs)) {
      nextAttrs += ' target="_blank"';
    }
    if (/^https?:\/\//i.test(href) && !/\brel\s*=/i.test(nextAttrs)) {
      nextAttrs += ' rel="noopener noreferrer"';
    }
    return `<a ${nextAttrs}>`;
  });
}

/**
 * Full HTML document for iframe srcDoc — isolates email CSS from the app and supports dark mode.
 */
export function buildEmailViewerSrcDoc(bodyFragment: string, opts: { dark?: boolean } = {}): string {
  // Emails that author their own colors (inline color/background styles,
  // bgcolor/color attributes, or a <style> block) are rendered on a light
  // canvas exactly as designed — rewriting near-black text for dark mode
  // produces unreadable light-on-light text over their retained white or
  // pastel backgrounds. Only unstyled fragments get the app dark theme.
  const isSelfStyled =
    /<\s*style\b/i.test(bodyFragment)
    || /\s(?:bgcolor|color)\s*=/i.test(bodyFragment)
    || /\bstyle\s*=\s*["'][^"']*(?:color|background)\s*:/i.test(bodyFragment);
  const dark = !!opts.dark && !isSelfStyled;
  const surface = dark ? '#111827' : '#ffffff';
  const text = dark ? '#e5e7eb' : '#111827';
  const link = dark ? '#93c5fd' : '#2563eb';
  const bqBorder = dark ? '#4b5563' : '#e5e7eb';
  const bqText = dark ? '#d1d5db' : '#4b5563';
  const subtleSurface = dark ? '#1f2937' : '#f9fafb';

  const darkModeSignatureColorFix = dark ? `
body [style*="color:#000" i],
body [style*="color: #000" i],
body [style*="color:#000000" i],
body [style*="color: #000000" i],
body [style*="color:black" i],
body [style*="color: black" i],
body [style*="color:#111" i],
body [style*="color: #111" i],
body [style*="color:#111111" i],
body [style*="color: #111111" i],
body [style*="color:#222" i],
body [style*="color: #222" i],
body [style*="color:#222222" i],
body [style*="color: #222222" i],
body [style*="color:#333" i],
body [style*="color: #333" i],
body [style*="color:#333333" i],
body [style*="color: #333333" i],
body [style*="color:rgb(0,0,0)" i],
body [style*="color: rgb(0,0,0)" i],
body [style*="color: rgb(0, 0, 0)" i],
body [style*="color:rgba(0,0,0,1)" i],
body [style*="color: rgba(0,0,0,1)" i],
body [style*="color: rgba(0, 0, 0, 1)" i],
body [style*="color:rgb(17,17,17)" i],
body [style*="color: rgb(17,17,17)" i],
body [style*="color: rgb(17, 17, 17)" i],
body [style*="color:rgb(34,34,34)" i],
body [style*="color: rgb(34,34,34)" i],
body [style*="color: rgb(34, 34, 34)" i],
body [style*="color:rgb(51,51,51)" i],
body [style*="color: rgb(51,51,51)" i],
body [style*="color: rgb(51, 51, 51)" i],
body [style*="-webkit-text-fill-color:#000" i],
body [style*="-webkit-text-fill-color: #000" i],
body [style*="-webkit-text-fill-color:#000000" i],
body [style*="-webkit-text-fill-color: #000000" i],
body [style*="-webkit-text-fill-color:black" i],
body [style*="-webkit-text-fill-color: black" i],
body font[color="#000" i],
body font[color="#000000" i],
body font[color="black" i] {
  color: ${text} !important;
  -webkit-text-fill-color: ${text} !important;
}

body [style*="background-color:#000" i],
body [style*="background-color: #000" i],
body [style*="background-color:#000000" i],
body [style*="background-color: #000000" i],
body [style*="background-color:black" i],
body [style*="background-color: black" i],
body [style*="background:#000" i],
body [style*="background: #000" i],
body [style*="background:#000000" i],
body [style*="background: #000000" i],
body [style*="background:black" i],
body [style*="background: black" i],
body [bgcolor="#000" i],
body [bgcolor="#000000" i],
body [bgcolor="black" i] {
  background-color: ${subtleSurface} !important;
  background: ${subtleSurface} !important;
}
` : '';

  const css = `
html,body{margin:0;padding:0;box-sizing:border-box;}
html{overflow-x:auto;}
body{
  font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
  font-size:14px;line-height:1.55;
  padding:12px;
  background:${surface};
  color:${text};
  word-wrap:break-word;overflow-wrap:anywhere;
  -webkit-text-size-adjust:100%;
}
a{color:${link};cursor:pointer;}
input[type="submit"],input[type="button"],button{cursor:pointer;}
blockquote{margin:0.5em 0;padding-left:12px;border-left:3px solid ${bqBorder};color:${bqText};}
img,video,embed,object,svg{max-width:100%!important;height:auto!important;}
table{border-collapse:collapse;max-width:none!important;}
th{white-space:nowrap;}
td{word-wrap:break-word;overflow-wrap:anywhere;}
pre,code{white-space:pre-wrap;word-wrap:break-word;}
div,section,article,main{max-width:100%;}
${darkModeSignatureColorFix}
  `.trim();

  // On Capacitor, add a <base> tag so any remaining relative URLs resolve correctly
  const baseTag = isCapacitor() ? `<base href="${getEndpointUrl('/')}">` : '';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="color-scheme" content="${dark ? 'dark' : 'light'}">${baseTag}<style>${css}</style></head><body>${bodyFragment}</body></html>`;
}

/**
 * Format sender name for display
 * Handles various sender formats and extracts display name
 */
export function formatSenderName(from: unknown): string {
  if (!from) return 'Unknown';

  // Handle string format
  if (typeof from === 'string') {
    const parsed = parseEmailString(from);
    return parsed.name || parsed.address || from;
  }

  // Handle object format { name, address } or { name, email }
  if (typeof from === 'object') {
    const obj = from as Record<string, unknown>;
    // Try various name fields
    const name = decodeMimeHeader(
      (typeof obj.name === 'string' ? obj.name : undefined)
      ?? (typeof obj.displayName === 'string' ? obj.displayName : undefined)
      ?? (typeof obj.display_name === 'string' ? obj.display_name : undefined)
      ?? ''
    ) || undefined;
    const email = (typeof obj.address === 'string' ? obj.address : undefined)
      ?? (typeof obj.email === 'string' ? obj.email : undefined);

    if (name && name !== email) return name;
    if (email) return email;
  }

  return 'Unknown';
}

/**
 * Get sender email address from various formats
 */
export function getSenderEmail(from: unknown): string {
  if (!from) return '';

  if (typeof from === 'string') {
    const parsed = parseEmailString(from);
    return parsed.address || from;
  }

  if (typeof from === 'object') {
    const obj = from as Record<string, unknown>;
    const addr = typeof obj.address === 'string' ? obj.address : undefined;
    const em = typeof obj.email === 'string' ? obj.email : undefined;
    return addr || em || '';
  }

  return '';
}

/**
 * Format file size for display (e.g., "1.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Get file icon class based on content type
 */
export function getFileIconClass(contentType: string): string {
  if (!contentType) return 'file';

  if (contentType.startsWith('image/')) return 'image';
  if (contentType.startsWith('video/')) return 'video';
  if (contentType.startsWith('audio/')) return 'audio';
  if (contentType.includes('pdf')) return 'pdf';
  if (contentType.includes('word') || contentType.includes('document')) return 'doc';
  if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'spreadsheet';
  if (contentType.includes('zip') || contentType.includes('archive')) return 'archive';

  return 'file';
}

// Shared interface for cached contact info
export interface CachedContact {
  email: string;
  personal_email?: string;
  primary_email?: string;
  all_emails?: string[];
  name?: string;
  avatar_url?: string;
}
